import { riskToneOf } from 'components/ScreeningComposer/riskTone';
import {
  VerificationPageState,
  VerificationSummary,
} from './verificationReportSummary';

/** The record page's sections, one status tab each. */
export type EntityHubSection = 'screening' | 'verification' | 'deep-dive';

/**
 * The dot beside a tab's status line. Risk and status colors only:
 * `running` is the one blue, `none` is grey (not run, unknown, turned off).
 */
export type SectionTone = 'high' | 'medium' | 'low' | 'running' | 'none';

export interface SectionStatus {
  text: string;
  tone: SectionTone;
}

const SECTIONS: EntityHubSection[] = ['screening', 'verification', 'deep-dive'];

/** Reads `?section=` from the URL; anything unknown is the screening tab. */
export const parseEntityHubSection = (
  value: string | null | undefined,
): EntityHubSection => {
  const section = String(value || '').toLowerCase() as EntityHubSection;
  return SECTIONS.includes(section) ? section : 'screening';
};

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

const pad = (value: number) => `${value < 10 ? '0' : ''}${value}`;

/** 0:42, 12:05, 1:02:05: a long check can run past the hour. */
export const clock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  return hours
    ? `${hours}:${pad(minutes)}:${pad(rest)}`
    : `${minutes}:${pad(rest)}`;
};

/** "Low risk · 12 matches", or what the screen is doing instead. */
export const screeningSectionStatus = ({
  screening,
  hasEvaluation,
  risk,
  matchCount,
}: {
  screening: boolean;
  hasEvaluation: boolean;
  risk: string;
  matchCount: number;
}): SectionStatus => {
  const matches = plural(matchCount, 'match', 'matches');
  if (screening) {
    return { text: `Screening now · ${matches} so far`, tone: 'running' };
  }
  if (hasEvaluation && risk) {
    const tone = riskToneOf(risk);
    return {
      text:
        tone === 'none' ? `${risk} · ${matches}` : `${risk} risk · ${matches}`,
      tone: tone,
    };
  }
  return {
    text: matchCount ? `Not evaluated · ${matches}` : 'No matches on file',
    tone: 'none',
  };
};

/** The verification page's own state, said in one line. */
export const verificationSectionStatus = (
  state: VerificationPageState,
  summary: VerificationSummary,
  elapsedSeconds: number | null,
  /** How many checks a run covers, from the record's checks list. */
  checkCount = 7,
): SectionStatus => {
  switch (state) {
    case 'running':
      return {
        text:
          elapsedSeconds === null
            ? 'Verifying'
            : `Verifying · ${Math.max(0, elapsedSeconds)}s`,
        tone: 'running',
      };
    case 'never':
      return { text: `Not run · ${checkCount} checks`, tone: 'none' };
    case 'noinput':
      return {
        text: `Needs details · 0 of ${checkCount} can run`,
        tone: 'medium',
      };
    case 'dboff':
      return { text: 'Turned off for this database', tone: 'none' };
    case 'noplan':
      return { text: 'Not on your plan', tone: 'none' };
    case 'error':
      return { text: `Interrupted · ${summary.tail}`, tone: 'high' };
    case 'failed':
      return { text: `${summary.figure} · ${summary.tail}`, tone: 'high' };
    case 'clean':
      return { text: `${summary.figure} checks passed`, tone: 'low' };
    case 'open': {
      const open = parseInt(summary.figure, 10);
      return {
        text: Number.isFinite(open)
          ? `${open} of ${checkCount} checks open`
          : `${summary.figure} · ${summary.tail}`,
        tone: 'medium',
      };
    }
    default:
      return { text: summary.figure, tone: 'none' };
  }
};

export type OwnershipInvestigationState =
  | 'loading'
  | 'no-access'
  | 'trial-used'
  | 'idle'
  | 'starting'
  | 'running'
  | 'done'
  | 'needs-review'
  | 'interrupted';

/** Whether the run left a report to read (and download). */
export const canReadOwnershipReport = (investigation: {
  ticketId: string | null;
  state: OwnershipInvestigationState;
  hasReport: boolean;
}): boolean =>
  Boolean(investigation.ticketId) &&
  (investigation.state === 'done' ||
    investigation.state === 'needs-review' ||
    (investigation.state === 'interrupted' && investigation.hasReport));

export const runCostLabel = (cost: number): string =>
  `${cost} credit${cost === 1 ? '' : 's'}`;

/**
 * Credits block STARTING a run, never reading one the team already paid for:
 * a finished check keeps its results and its report, and only the button that
 * would spend again is withheld.
 */
export const runBlockedByCredits = (
  noCredits: boolean,
  run: { running: boolean; finished: boolean },
): boolean => noCredits && !run.running && !run.finished;

export const noCreditsCopy = (
  cost: number,
): { pill: string; blurb: string } => ({
  pill: 'Out of credits',
  blurb: `This run costs ${runCostLabel(
    cost,
  )} and your team does not have enough remaining. Buy more to run it.`,
});

/** One Deep Dive tool, as its tab status line reads it. */
export interface DeepDiveTool {
  name: string;
  state: 'loading' | 'unavailable' | 'idle' | 'running' | 'done' | 'attention';
  elapsedSeconds: number;
  finishedHere: boolean;
}

const INVESTIGATION_TOOL_STATE: Record<
  OwnershipInvestigationState,
  DeepDiveTool['state']
> = {
  loading: 'loading',
  'no-access': 'unavailable',
  'trial-used': 'unavailable',
  idle: 'idle',
  starting: 'running',
  running: 'running',
  done: 'done',
  'needs-review': 'attention',
  interrupted: 'attention',
};

export const investigationTool = (
  investigation: OwnershipInvestigationState,
  elapsedSeconds: number,
  finishedHere: boolean,
): DeepDiveTool => ({
  name: 'Beneficial Ownership',
  state: INVESTIGATION_TOOL_STATE[investigation],
  elapsedSeconds: elapsedSeconds,
  finishedHere: finishedHere,
});

/** What the Deep Dive tools are doing: running, ready, or not run. */
export const deepDiveSectionStatus = (tools: DeepDiveTool[]): SectionStatus => {
  const running = tools.filter((tool) => tool.state === 'running');
  if (running.length > 1) {
    return { text: `${running.length} checks running`, tone: 'running' };
  }
  if (running.length === 1) {
    return {
      text: `${running[0].name} running · ${clock(running[0].elapsedSeconds)}`,
      tone: 'running',
    };
  }
  const review = tools.filter((tool) => tool.state === 'attention');
  if (review.length) {
    return { text: `${review[0].name} needs review`, tone: 'medium' };
  }
  const newResults = tools.filter(
    (tool) => tool.state === 'done' && tool.finishedHere,
  );
  if (newResults.length) {
    return {
      text:
        newResults.length === 1
          ? `Results ready · ${newResults[0].name}`
          : `${newResults.length} results ready`,
      tone: 'low',
    };
  }
  if (tools.some((tool) => tool.state === 'loading')) {
    return { text: 'Loading checks', tone: 'none' };
  }
  const runnable = tools.filter((tool) => tool.state !== 'unavailable');
  const notRun = runnable.filter((tool) => tool.state === 'idle').length;
  if (!runnable.length) return { text: 'Needs a Business plan', tone: 'none' };
  if (notRun === runnable.length) {
    return {
      text: `${notRun} ${notRun === 1 ? 'check' : 'checks'} not run`,
      tone: 'none',
    };
  }
  if (notRun) {
    return {
      text: `${notRun} of ${runnable.length} checks not run`,
      tone: 'none',
    };
  }
  return {
    text:
      runnable.length === 1
        ? `${runnable[0].name} on file`
        : `${runnable.length} reports on file`,
    tone: 'none',
  };
};
